// FAQ content for /support and /faq. Answers are markdown (rendered with markdown-it).
// Source: MANUEL_UTILISATEUR.md §9, plus ticket behaviour from features/support.md.
// Keep answers in sync with the manual until both are reworked (see features/docs.md).

export const FAQ_CATEGORIES = [
  { id: 'start', title: 'Démarrer', icon: 'mdi-rocket-launch-outline' },
  { id: 'projects', title: 'Chantiers & devis', icon: 'mdi-clipboard-text-outline' },
  { id: 'assistant', title: "L'assistant", icon: 'mdi-robot-outline' },
  { id: 'account', title: 'Compte & offre', icon: 'mdi-account-outline' },
  { id: 'support', title: 'Support & tickets', icon: 'mdi-lifebuoy' },
]

export const FAQ_ITEMS = [
  {
    slug: 'chantier-ou-conversation',
    category: 'start',
    featured: true,
    question: 'Quelle est la différence entre un chantier et une conversation ?',
    answer: `Un **chantier** correspond à un devis quantitatif chiffré : l'assistant vous pose des questions puis génère un brief avec un tableau de matériaux exportable.

Une **conversation** est un échange libre en langage naturel, sans tableau ni devis.

C'est l'assistant qui choisit automatiquement l'un ou l'autre à partir de votre premier message. Décrivez donc votre besoin de façon chiffrée (surfaces, ouvrages, contraintes) si vous voulez obtenir un devis.`,
    related: ['transformer-conversation', 'retrouver-chantier'],
  },
  {
    slug: 'transformer-conversation',
    category: 'start',
    question: 'Puis-je transformer une conversation en chantier, ou inversement ?',
    answer: `Non, pas directement depuis l'interface actuelle.

Si vous avez besoin d'un devis chiffré après avoir commencé une conversation, démarrez un nouveau chantier via **« Nouveau »** en décrivant votre besoin de façon chiffrée.`,
    related: ['chantier-ou-conversation'],
  },
  {
    slug: 'retrouver-chantier',
    category: 'start',
    question: 'Je ne retrouve pas un ancien chantier ou une ancienne conversation. Que faire ?',
    answer: `Utilisez **« Recherche »** dans le panneau latéral.

Vos chantiers et conversations sont systématiquement sauvegardés côté serveur, associés à votre compte : il n'existe aucun réglage de sauvegarde à activer.`,
    related: ['autre-appareil', 'suppression-par-erreur'],
  },
  {
    slug: 'autre-appareil',
    category: 'start',
    question: 'Mes chantiers et conversations sont-ils accessibles depuis un autre ordinateur ?',
    answer: `Oui : ils sont sauvegardés côté serveur et liés à votre compte, quel que soit l'appareil utilisé pour vous connecter.`,
    related: ['retrouver-chantier', 'utiliser-sur-mobile'],
  },
  {
    slug: 'utiliser-sur-mobile',
    category: 'start',
    question: 'Puis-je utiliser DuoMat sur mon téléphone ?',
    answer: `Oui, l'interface est responsive. Certains éléments (comme le sélecteur de moteur IA) sont masqués sur mobile pour des raisons d'espace.`,
    related: ['autre-appareil'],
  },
  {
    slug: 'exporter-devis',
    category: 'projects',
    featured: true,
    question: 'Comment exporter mon devis ?',
    answer: `Depuis le brief d'un chantier, utilisez **« Export CSV »** ou **« Export PDF »** au-dessus du tableau de matériaux.

L'export ERP est en cours de finalisation.`,
    related: ['chantier-ou-conversation', 'brief-douteux'],
  },
  {
    slug: 'brief-douteux',
    category: 'projects',
    question: 'Le brief généré ne correspond pas à mon besoin. Que faire ?',
    answer: `Utilisez le panneau de **retour sur le brief** pour noter sa qualité et décrire ce qui ne va pas. Cochez **« Ouvrir un ticket »** pour que l'équipe support reçoive votre retour avec le brief déjà lié : elle verra exactement ce que vous voyez.

Vous pouvez aussi poser des questions de suivi à l'assistant pour affiner le brief.`,
    related: ['ouvrir-un-ticket', 'references-inventees'],
  },
  {
    slug: 'suppression-par-erreur',
    category: 'projects',
    question: "J'ai supprimé un chantier ou une conversation par erreur. Puis-je le récupérer ?",
    answer: `Non, la suppression est irréversible et il n'existe pas de fonction d'export/import de secours.

Si la donnée perdue était critique, [ouvrez un ticket](/tickets/new) en précisant le chantier concerné.`,
    related: ['retrouver-chantier', 'ouvrir-un-ticket'],
  },
  {
    slug: 'reponses-generiques',
    category: 'assistant',
    featured: true,
    question: 'Les réponses sont génériques : l\'assistant ne semble pas connaître nos produits. Pourquoi ?',
    answer: `La base documentaire de l'assistant est alimentée par synchronisation depuis Google Drive, gérée par vos administrateurs.

Si des documents récents n'ont pas encore été synchronisés, signalez-le à votre administrateur ou [ouvrez un ticket](/tickets/new).`,
    related: ['references-inventees', 'ouvrir-un-ticket'],
  },
  {
    slug: 'references-inventees',
    category: 'assistant',
    question: "L'assistant invente-t-il des références produits ?",
    answer: `Non : l'assistant est conçu pour signaler explicitement lorsqu'il n'est pas certain d'une référence plutôt que d'en inventer une.

Si une réponse vous semble douteuse, demandez-lui de préciser la source de sa recommandation, et signalez-le au support si le problème persiste.`,
    related: ['reponses-generiques', 'brief-douteux'],
  },
  {
    slug: 'api-reelle-ou-simulation',
    category: 'assistant',
    question: "Comment savoir si l'assistant utilise l'API réelle ou une simulation ?",
    answer: `Cette information n'est visible que pour les comptes administrateurs/staff, dans **Réglages → Intelligence Artificielle**.

Les comptes standards reçoivent toujours les réponses telles que configurées par leur administrateur.`,
    related: ['reponses-generiques'],
  },
  {
    slug: 'modifier-identifiants',
    category: 'account',
    question: "Puis-je modifier mon nom d'utilisateur ou mon adresse e-mail ?",
    answer: `Non, pas depuis les Réglages : ces informations sont en lecture seule et gérées par votre administrateur.

Pour une demande de changement, [ouvrez un ticket](/tickets/new) dans la catégorie **Compte**.`,
    related: ['quota-atteint', 'ouvrir-un-ticket'],
  },
  {
    slug: 'quota-atteint',
    category: 'account',
    question: 'Que se passe-t-il si mon quota est atteint ?',
    answer: `Consultez la section **« Offre »** de vos Réglages pour vérifier votre consommation.

Si un plafond est atteint, contactez votre administrateur.`,
    related: ['modifier-identifiants'],
  },
  {
    slug: 'ouvrir-un-ticket',
    category: 'support',
    featured: true,
    question: 'Comment contacter le support ?',
    answer: `Ouvrez un **ticket** depuis le [Centre d'aide](/support) ou depuis la page [Tickets](/tickets), avec le bouton **« Nouveau ticket »** :

1. indiquez un sujet et une catégorie (bug, question, qualité du brief, catalogue / stock, compte, autre) ;
2. décrivez votre demande le plus précisément possible ;
3. envoyez : vous êtes redirigé vers le ticket, où vous pouvez échanger avec l'équipe.

Depuis le panneau de retour sur un brief, la case **« Ouvrir un ticket »** crée un ticket déjà lié au brief concerné.`,
    related: ['suivre-un-ticket', 'qui-voit-mes-tickets'],
  },
  {
    slug: 'suivre-un-ticket',
    category: 'support',
    question: "Comment suivre l'avancement de mon ticket ?",
    answer: `Chaque ticket affiche un **statut** : *Ouvert*, *En cours*, *En attente de l'utilisateur*, *Résolu* ou *Fermé*.

Les réponses de l'équipe et les changements de statut déclenchent une notification dans l'application (la cloche en haut de l'écran). Aucun e-mail n'est envoyé pour l'instant : pensez à consulter vos [tickets](/tickets) régulièrement.

Quand un ticket est *Résolu*, vous pouvez le rouvrir en répondant. Sans réponse de votre part, il est fermé automatiquement au bout de 7 jours.`,
    related: ['ouvrir-un-ticket', 'qui-voit-mes-tickets'],
  },
  {
    slug: 'qui-voit-mes-tickets',
    category: 'support',
    question: 'Qui peut voir mes tickets ?',
    answer: `Un ticket est visible par son auteur, par les administrateurs de son équipe et de son organisation, et par l'équipe support DuoMat.

Les notes internes de l'équipe support ne sont jamais visibles par les utilisateurs.`,
    related: ['ouvrir-un-ticket', 'suivre-un-ticket'],
  },
]

export const faqBySlug = (slug) => FAQ_ITEMS.find((i) => i.slug === slug)
export const faqCategory = (id) => FAQ_CATEGORIES.find((c) => c.id === id)
export const featuredFaq = () => FAQ_ITEMS.filter((i) => i.featured)

/** Case/accent-insensitive search over question and answer. */
export const searchFaq = (q, items = FAQ_ITEMS) => {
  const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  const terms = norm(q || '').split(/\s+/).filter(Boolean)
  if (!terms.length) return items
  return items.filter((i) => {
    const hay = norm(`${i.question} ${i.answer}`)
    return terms.every((t) => hay.includes(t))
  })
}
